import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { authClient, useSession } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useTheme } from "@/lib/theme";
import heroLight from "@/assets/hero-light.png";
import heroDark from "@/assets/hero-dark.png";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

const DAILY_HOME_ACTIONS = [
  "Accesos",
  "Invitaciones",
  "Reservas",
  "Y mucho más",
] as const;

function LoginPage() {
  const navigate = useNavigate();
  const { effectiveTheme } = useTheme();
  const { data: session, isPending: isLoading } = useSession();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch available auth providers
  const { data: providers } = trpc.auth.getAvailableProviders.useQuery();

  // Redirect if already logged in
  useEffect(() => {
    if (!isLoading && session?.user) {
      navigate({ to: "/" });
    }
  }, [isLoading, session?.user, navigate]);

  if (!isLoading && session?.user) return null;

  const handleOAuthLogin = async (provider: string) => {
    await authClient.signIn.social({
      provider,
      callbackURL: import.meta.env.VITE_WEB_URL,
    });
  };

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await authClient.signIn.magicLink({
        email,
        callbackURL: import.meta.env.VITE_WEB_URL,
      });

      if (result.error) {
        console.error("Email login error:", result.error);
        toast.error("No se pudo enviar el email. Intentá de nuevo.");
      } else {
        toast.success("Revisá tu email para el enlace de acceso");
        setEmail("");
      }
    } catch (error) {
      console.error("Email login error:", error);
      toast.error("No se pudo enviar el email. Intentá de nuevo.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const showGoogleLogin = providers?.google ?? true;
  const showAppleLogin = providers?.apple ?? false;
  const heroImage = effectiveTheme === "dark" ? heroDark : heroLight;

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-0 lg:hidden">
        <img
          src={heroImage}
          alt="Paisaje natural del barrio"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-background/70" />
      </div>

      <div className="pointer-events-none absolute inset-0 hidden bg-gradient-to-br from-primary/18 via-background to-amber-100/45 dark:to-amber-950/20 lg:block" />
      <div className="pointer-events-none absolute inset-0 hidden lg:block">
        <div className="absolute -top-56 -left-32 h-[30rem] w-[30rem] rounded-full bg-primary/25 blur-3xl" />
        <div className="absolute right-[-8rem] bottom-[-8rem] h-[24rem] w-[24rem] rounded-full bg-amber-300/40 blur-3xl dark:bg-amber-600/15" />
      </div>

      <div className="relative mx-auto flex min-h-screen w-full max-w-6xl items-end px-4 py-6 sm:px-6 lg:items-center lg:px-10 lg:py-8">
        <div className="grid w-full items-center gap-6 lg:grid-cols-[1.12fr_0.88fr] lg:gap-8">
          <div className="hidden lg:block">
            <div className="relative min-h-[38rem] overflow-hidden rounded-[2rem] border border-border/70 shadow-2xl animate-in fade-in zoom-in-95 duration-700">
              <img
                src={heroImage}
                alt="Paisaje de barrio privado con laguna, arboleda y casa de campo"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/10" />
              <div className="absolute inset-0 flex flex-col justify-between p-8 text-white">
                <div className="inline-flex w-fit items-center rounded-full border border-white/35 bg-black/25 px-3 py-1 text-xs font-medium backdrop-blur-sm">
                  Clara
                </div>

                <div className="space-y-5">
                  <h1 className="max-w-md text-4xl leading-tight font-semibold text-balance">
                    Tu hogar, en orden.
                  </h1>
                  <p className="max-w-md text-sm text-white/90">
                    Gestión cotidiana de tu barrio, simple y clara.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {DAILY_HOME_ACTIONS.map((action) => (
                      <span
                        key={action}
                        className="rounded-full border border-white/30 bg-black/20 px-3 py-1 text-xs font-medium text-white/90 backdrop-blur-sm"
                      >
                        {action}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mx-auto w-full max-w-lg lg:max-w-md">
            <div className="rounded-[2rem] border border-white/45 bg-white/62 p-5 shadow-2xl backdrop-blur-2xl animate-in fade-in slide-in-from-bottom-6 duration-700 dark:border-white/15 dark:bg-black/34 sm:p-7 lg:bg-white/54 dark:lg:bg-black/30">
              <div className="mb-6 space-y-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/80 px-3 py-1 text-xs font-medium text-muted-foreground">
                  <span className="h-2 w-2 rounded-full bg-primary" />
                  Tu espacio diario
                </div>
                <h2 className="text-3xl font-semibold tracking-tight">
                  Bienvenido a Clara
                </h2>
                <p className="text-sm text-muted-foreground">
                  Iniciá sesión para continuar.
                </p>
              </div>

              <div className="space-y-2">
                {showGoogleLogin && (
                  <Button
                    variant="outline"
                    className="h-11 w-full rounded-xl bg-background/75"
                    onClick={() => handleOAuthLogin("google")}
                  >
                    <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    Continuar con Google
                  </Button>
                )}

                {showAppleLogin && (
                  <Button
                    variant="outline"
                    className="h-11 w-full rounded-xl bg-background/75"
                    onClick={() => handleOAuthLogin("apple")}
                  >
                    <svg
                      className="mr-2 h-4 w-4"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                    </svg>
                    Continuar con Apple
                  </Button>
                )}
              </div>

              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border/80" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-card px-2 text-[11px] font-semibold tracking-[0.12em] text-muted-foreground uppercase">
                    O continuá con email
                  </span>
                </div>
              </div>

              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11 rounded-xl bg-background/75"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="h-11 w-full rounded-xl text-base font-semibold"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Enviando..." : "Enviar enlace de acceso"}
                </Button>
              </form>

              <p className="mt-5 text-center text-xs text-muted-foreground">
                Al continuar, aceptás nuestros{" "}
                <a href="/terms" className="underline underline-offset-2">
                  Términos de Servicio
                </a>{" "}
                y{" "}
                <a href="/privacy" className="underline underline-offset-2">
                  Política de Privacidad
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
